import { polygonRings, polygonVertexLocation, replacePolygonRing } from "@constructionautomation/backend/convex/lib/measurementGeometry";
import { notifyError } from "@/lib/notify-error";
import { undoRedoErrorMessage } from "@/lib/undo-redo-error";
import { useCallback, useMemo } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { useDocumentViewport } from "@/features/document-viewport/context";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import { DEFAULT_TEXT_FONT_SIZE } from "@/hooks/useMeasurementTextLock";
import type { Point } from "@/lib/geometry";
import {
  DraftMeasurementCard,
  MeasurementContextMenu,
  SelectedMeasurementCard,
} from "@/viewport-tools/measurement/overlay/MeasurementOverlayPanels";
import {
  BoxSelectionLayer,
  DraftMeasurementPreviewLayer,
  PersistedMeasurementsLayer,
} from "@/viewport-tools/measurement/overlay/MeasurementOverlayLayers";
import { saveMeasurementEdits } from "@/viewport-tools/measurement/overlay/measurement-overlay-edit-actions";
import {
  computeValue,
  getClosestSegmentInsertionPoint,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import {
  type DraftMeasurementState,
  MEASUREMENT_GEOMETRY_TYPES,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type MeasurementOverlayRecord,
  type MeasurementTagOption,
  type PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import {
  type MeasurementType,
  TOOL_IDS,
  type ToolType,
} from "@/viewport-tools/measurement";
import { useMeasurementOverlayInteractions } from "@/viewport-tools/measurement/overlay/useMeasurementOverlayInteractions";
import type { PdfSnapStatus } from "@/viewport-tools/measurement/overlay/interactions/useMeasurementOverlayPdfSnap";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

interface MeasurementToolConfig {
  scale: MeasurementScale | null;
  activeTool: ToolType | null;
  /** Tracing must start through saved geometry; standard tools retain select/move priority. */
  interactionMode?: "standard" | "trace";
  activeMeasurementType: MeasurementType | null;
  currentHeightFeet?: number;
  snapEnabled: boolean;
  angleLockEnabled?: boolean;
  incrementSnapEnabled: boolean;
  incrementStepFeet: number;
  textLocked?: boolean;
  textFontSize?: number;
}

interface TagDisplayData {
  colorByTagId: Map<string, string>;
  labelByTagId: Map<string, string>;
  /** Optional second label line per record (keyed like `labelByTagId`), shown under the name. */
  annotationByTagId?: Map<string, string>;
  tags: MeasurementTagOption[];
}

interface DraftMeasurementCallbacks {
  draftMeasurement: DraftMeasurementState | null;
  draftTagId: Id<"takeoffPilotExtractedTags"> | null;
  onDraftMeasurementChange: (draft: DraftMeasurementState | null) => void;
  onDraftTagChange: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  onCreateCustomLineItem?: (args: {
    rawValue: string;
    unitCost: number | null;
  }) => Promise<Id<"takeoffPilotExtractedTags"> | void>;
  onSaveDraftMeasurement: () => Promise<void>;
  onCancelDraftMeasurement: () => void;
  isSavingDraftMeasurement?: boolean;
}

interface MeasurementSelectionState {
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  activeMeasurementId: Id<"takeoffPilotMeasurements"> | null;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => void;
  onSelectMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    options?: { additive?: boolean }
  ) => void;
  onReassignMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  onReassignMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  isReassigningMeasurementId?: Id<"takeoffPilotMeasurements"> | null;
  isDeletingMeasurementId?: Id<"takeoffPilotMeasurements"> | null;
  onDeleteMeasurement: (measurementId: Id<"takeoffPilotMeasurements">) => Promise<void>;
  onDeleteMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void>;
  onApplyMeasurementLeaders?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => Promise<boolean>;
  onGroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onUngroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onGroupAllLabelsInScope?: () => Promise<boolean>;
  onUngroupAllLabelsInScope?: () => Promise<boolean>;
  /** Optional per-measurement action; when set, a "Milestones" item appears in the context menu. */
  onOpenMilestones?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
}

interface MeasurementCrudOperations {
  measurements: MeasurementOverlayRecord[];
  onUpdateMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => Promise<void>;
  onUpdateMeasurementAnnotation?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => Promise<void>;
}

interface ClipboardOperations {
  canPasteMeasurement?: boolean;
  onCopyMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  onCopyMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => void;
  onPasteMeasurement?: (point?: Point) => Promise<void> | void;
  onDuplicateMeasurement?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    point?: Point
  ) => Promise<void> | void;
  onDuplicateMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    point?: Point
  ) => Promise<void> | void;
}

interface MeasurementOverlayPresentation {
  showDraftMeasurementCard?: boolean;
  showSelectedMeasurementCard?: boolean;
  showLabels?: boolean;
  showLeaders?: boolean;
  showValues?: boolean;
}

interface MeasurementOverlayProps
  extends MeasurementToolConfig,
    TagDisplayData,
    DraftMeasurementCallbacks,
    MeasurementSelectionState,
    MeasurementCrudOperations,
    ClipboardOperations,
    MeasurementOverlayPresentation {}

function getPdfSnapStatusLabel(status: PdfSnapStatus): string | null {
  switch (status) {
    case "loading":
      return "Preparing snap geometry";
    case "empty":
      return "No snap geometry on this page";
    case "unavailable":
      return "Snap geometry unavailable";
    case "error":
      return "Snap geometry failed to load";
    default:
      return null;
  }
}

function getVisibleSnapMarkerPoint(
  snapPoint: Point | null,
  activeTool: ToolType | null,
  activeMeasurementType: MeasurementType | null,
  interaction: { mode: string } | null
): Point | null {
  const isVisible = Boolean(
    snapPoint &&
      activeTool &&
      activeMeasurementType &&
      (!interaction || interaction.mode === "drawing")
  );
  return isVisible ? snapPoint : null;
}

/** Drops the drawing tool and paste/duplicate actions for viewers who can't edit. */
function gateEditing<T extends {
  showDraftMeasurementCard: boolean;
  showSelectedMeasurementCard: boolean;
  activeTool: ToolType | null;
  canPasteMeasurement: boolean;
  onPasteMeasurement?: unknown;
  onDuplicateMeasurement?: unknown;
  onDuplicateMeasurements?: unknown;
}>(canEdit: boolean, editing: T): T {
  if (canEdit) return editing;
  return {
    ...editing,
    showDraftMeasurementCard: false,
    showSelectedMeasurementCard: false,
    activeTool: null,
    canPasteMeasurement: false,
    onPasteMeasurement: undefined,
    onDuplicateMeasurement: undefined,
    onDuplicateMeasurements: undefined,
  };
}

/** A read-only layer never takes pointer input, so the viewport keeps pan and zoom. */
function acceptsPointerInput(canEdit: boolean, reasons: unknown[]) {
  return canEdit && reasons.some(Boolean);
}

/** Read-only and trace layers never start edits on saved geometry; trace clicks bubble to drawing. */
function gateLayerHandlers<T extends Record<string, (...args: never[]) => void>>(
  canEdit: boolean,
  handlers: T,
  interactionMode?: MeasurementToolConfig["interactionMode"]
): T {
  if (canEdit && interactionMode !== "trace") return handlers;
  const ignore = () => {};
  return Object.fromEntries(Object.keys(handlers).map((key) => [key, ignore])) as T;
}

function getValueIncrementFeet(enabled: boolean, incrementStepFeet: number) {
  return enabled ? incrementStepFeet : undefined;
}

export function MeasurementOverlay({
  scale,
  interactionMode,
  activeTool: requestedActiveTool,
  activeMeasurementType,
  currentHeightFeet,
  snapEnabled,
  angleLockEnabled = false,
  incrementSnapEnabled,
  incrementStepFeet,
  textLocked = false,
  textFontSize = DEFAULT_TEXT_FONT_SIZE,
  measurements,
  colorByTagId,
  labelByTagId,
  annotationByTagId,
  draftMeasurement,
  draftTagId,
  tags,
  onDraftMeasurementChange,
  onDraftTagChange,
  onCreateCustomLineItem,
  onSaveDraftMeasurement,
  onCancelDraftMeasurement,
  isSavingDraftMeasurement = false,
  selectedMeasurementIds,
  activeMeasurementId,
  onSelectMeasurement,
  onSelectMeasurements,
  onUpdateMeasurement,
  onUpdateMeasurementAnnotation,
  onReassignMeasurement,
  onReassignMeasurements,
  isReassigningMeasurementId = null,
  isDeletingMeasurementId = null,
  onDeleteMeasurement,
  onDeleteMeasurements,
  onApplyMeasurementLeaders,
  onGroupLabels,
  onUngroupLabels,
  onGroupAllLabelsInScope,
  onUngroupAllLabelsInScope,
  onOpenMilestones,
  canPasteMeasurement: requestedCanPasteMeasurement = false,
  onCopyMeasurement,
  onCopyMeasurements,
  onPasteMeasurement: requestedOnPasteMeasurement,
  onDuplicateMeasurement: requestedOnDuplicateMeasurement,
  onDuplicateMeasurements: requestedOnDuplicateMeasurements,
  showDraftMeasurementCard: requestedShowDraftMeasurementCard = true,
  showSelectedMeasurementCard: requestedShowSelectedMeasurementCard = true,
  showLabels = true,
  showLeaders = true,
  showValues = true,
}: MeasurementOverlayProps) {
  const viewport = useDocumentViewport();
  // Every measurement edit is a project write. Viewers who can't edit (reviewers) get a static
  // layer: no drawing tool, drags, cards, context menu, or paste/duplicate shortcuts.
  const canEdit = useCanEdit();
  const {
    activeTool,
    canPasteMeasurement,
    onPasteMeasurement,
    onDuplicateMeasurement,
    onDuplicateMeasurements,
    showDraftMeasurementCard,
    showSelectedMeasurementCard,
  } = gateEditing(canEdit, {
    showDraftMeasurementCard: requestedShowDraftMeasurementCard,
    showSelectedMeasurementCard: requestedShowSelectedMeasurementCard,
    activeTool: requestedActiveTool,
    canPasteMeasurement: requestedCanPasteMeasurement,
    onPasteMeasurement: requestedOnPasteMeasurement,
    onDuplicateMeasurement: requestedOnDuplicateMeasurement,
    onDuplicateMeasurements: requestedOnDuplicateMeasurements,
  });
  const valueIncrementFeet = getValueIncrementFeet(
    incrementSnapEnabled,
    incrementStepFeet
  );
  const {
    overlayRef,
    pdfSnapStatus,
    snapHoverPoint,
    interaction,
    drawingDraft,
    previewAnnotations,
    previewGeometries,
    errorMessage,
    contextMenu,
    latestDraftMeasurement,
    handleCancelDraft,
    handleSaveDraft,
    handleStageMouseDown,
    handleStageContextMenu,
    handleStageMouseMove,
    handleStageMouseUp,
    handleStageMouseLeave,
    handleOverlayPointerEnter,
    handleOverlayPointerLeave,
    selectionModifierPressed,
    startDraftMove,
    openContextMenu,
    startMeasurementMove,
    startMeasurementResize,
    startMeasurementVertexDrag,
    openVertexContextMenu,
    startMeasurementLabelMove,
    startMeasurementLabelResize,
    closeContextMenu,
  } = useMeasurementOverlayInteractions({
    viewport,
    scale,
    activeTool,
    activeMeasurementType,
    currentHeightFeet,
    snapEnabled,
    angleLockEnabled,
    incrementSnapEnabled,
    incrementStepFeet,
    measurements,
    draftMeasurement,
    selectedMeasurementIds,
    activeMeasurementId,
    canPasteMeasurement,
    onDraftMeasurementChange,
    onSaveDraftMeasurement,
    onCancelDraftMeasurement,
    onSelectMeasurement,
    onSelectMeasurements,
    onUpdateMeasurement,
    onUpdateMeasurementAnnotation,
    onCopyMeasurement,
    onCopyMeasurements,
    onPasteMeasurement,
    onDuplicateMeasurement,
    onDuplicateMeasurements,
  });
  // A menu opened before a demotion would otherwise stay open with its write actions.
  useOnEditAccessLost(closeContextMenu);

  const handleSaveMeasurementEdits = useCallback(
    async (
      measurementId: Id<"takeoffPilotMeasurements">,
      edits: { valueInput: string; valueChanged: boolean; heightInput: string; heightChanged: boolean }
    ) => {
      await saveMeasurementEdits({
        measurementId,
        edits,
        measurements,
        scale,
        onUpdateMeasurement,
      });
    },
    [measurements, onUpdateMeasurement, scale]
  );

  const handleInsertMeasurementPoint = useCallback(
    async (measurementId: Id<"takeoffPilotMeasurements">, pagePoint: Point) => {
      const measurement = measurements.find((item) => item._id === measurementId);
      if (
        !measurement ||
        measurement.geometry.type !== MEASUREMENT_GEOMETRY_TYPES.poly ||
        (measurement.tool !== TOOL_IDS.polyline && measurement.tool !== TOOL_IDS.polygon)
      ) {
        return;
      }

      const isClosed = measurement.tool === TOOL_IDS.polygon;
      const candidates = polygonRings(measurement.geometry).flatMap((ring, ringIndex) => {
        const insertion = getClosestSegmentInsertionPoint(ring, pagePoint, isClosed);
        return insertion ? [{ ...insertion, ringIndex, ring,
          distance: Math.hypot(insertion.point.x - pagePoint.x, insertion.point.y - pagePoint.y) }] : [];
      }).sort((a, b) => a.distance - b.distance);
      const insertion = candidates[0];
      if (!insertion) return;
      const nextVertices = [...insertion.ring];
      nextVertices.splice(insertion.segmentIndex + 1, 0, insertion.point);
      const nextGeometry = replacePolygonRing(measurement.geometry, insertion.ringIndex, nextVertices);
      const nextValue = scale
        ? computeValue(
            measurement.tool,
            measurement.measurementType,
            nextGeometry,
            measurement.height,
            scale.pixelsPerFoot,
            scale.pageAspectRatio,
            valueIncrementFeet
          )
        : measurement.value;

      try {
        await onUpdateMeasurement(measurement._id, nextGeometry, measurement.height, nextValue);
      } catch (error) {
        console.error("Failed to insert measurement point:", error);
        notifyError(error, undoRedoErrorMessage(error, "Failed to insert point"));
      }
    },
    [measurements, onUpdateMeasurement, scale, valueIncrementFeet]
  );

  const handleRemoveMeasurementPoint = useCallback(
    async (measurementId: Id<"takeoffPilotMeasurements">, vertexIndex: number) => {
      const measurement = measurements.find((item) => item._id === measurementId);
      if (
        !measurement ||
        measurement.geometry.type !== MEASUREMENT_GEOMETRY_TYPES.poly ||
        (measurement.tool !== TOOL_IDS.polyline && measurement.tool !== TOOL_IDS.polygon)
      ) {
        return;
      }

      const minVertexCount = measurement.tool === TOOL_IDS.polygon ? 3 : 2;
      const location = polygonVertexLocation(measurement.geometry, vertexIndex);
      if (!location || location.ring.length <= minVertexCount) return;
      const nextGeometry = replacePolygonRing(measurement.geometry, location.ringIndex,
        location.ring.filter((_, index) => index !== location.vertexIndex));
      const nextValue = scale
        ? computeValue(
            measurement.tool,
            measurement.measurementType,
            nextGeometry,
            measurement.height,
            scale.pixelsPerFoot,
            scale.pageAspectRatio,
            valueIncrementFeet
          )
        : measurement.value;

      try {
        await onUpdateMeasurement(measurement._id, nextGeometry, measurement.height, nextValue);
      } catch (error) {
        console.error("Failed to remove measurement point:", error);
        notifyError(error, undoRedoErrorMessage(error, "Failed to remove point"));
      }
    },
    [measurements, onUpdateMeasurement, scale, valueIncrementFeet]
  );

  const selectedMeasurements = useMemo(() => {
    if (selectedMeasurementIds.length <= 1) {
      return [];
    }
    return measurements.filter((measurement) => selectedMeasurementIds.includes(measurement._id));
  }, [measurements, selectedMeasurementIds]);

  const contextMeasurementId =
    contextMenu?.target.type === "measurement" || contextMenu?.target.type === "measurement-vertex"
      ? contextMenu.target.measurementId
      : null;
  const overlayAcceptsPointerEvents = acceptsPointerInput(canEdit, [
    activeTool,
    interaction,
    selectedMeasurementIds.length > 0,
    canPasteMeasurement,
    selectionModifierPressed,
  ]);
  const snapHoverScreenPoint = snapHoverPoint
    ? viewport.transforms.pageToScreenPoint(snapHoverPoint)
    : null;
  const visibleSnapHoverScreenPoint = getVisibleSnapMarkerPoint(
    snapHoverScreenPoint,
    activeTool,
    activeMeasurementType,
    interaction
  );
  const pdfSnapStatusLabel = getPdfSnapStatusLabel(pdfSnapStatus);

  return (
    <div
      ref={overlayRef}
      className={`absolute inset-0 z-30 ${overlayAcceptsPointerEvents ? "pointer-events-auto" : "pointer-events-none"}`}
      onMouseDown={handleStageMouseDown}
      {...gateLayerHandlers(canEdit, { onContextMenu: handleStageContextMenu })}
      onMouseMove={handleStageMouseMove}
      onMouseUp={() => void handleStageMouseUp()}
      onMouseLeave={handleStageMouseLeave}
      onPointerEnter={handleOverlayPointerEnter}
      onPointerLeave={handleOverlayPointerLeave}
    >
      <PersistedMeasurementsLayer
        viewport={viewport}
        measurements={measurements}
        selectedMeasurementIds={selectedMeasurementIds}
        previewAnnotations={previewAnnotations}
        previewGeometries={previewGeometries}
        colorByTagId={colorByTagId}
        labelByTagId={labelByTagId}
        annotationByTagId={annotationByTagId}
        scale={scale}
        valueIncrementFeet={valueIncrementFeet}
        textLocked={textLocked}
        textFontSize={textFontSize}
        showLabels={showLabels}
        showLeaders={showLeaders}
        showValues={showValues}
        {...gateLayerHandlers(canEdit, {
          startMeasurementMove,
          startMeasurementResize,
          startMeasurementVertexDrag,
          openVertexContextMenu,
          startMeasurementLabelMove,
          startMeasurementLabelResize,
          openContextMenu,
        }, interactionMode)}
      />

      <DraftMeasurementPreviewLayer
        viewport={viewport}
        drawingDraft={drawingDraft}
        draftMeasurement={draftMeasurement}
        interaction={interaction}
        startDraftMove={startDraftMove}
      />

      <BoxSelectionLayer viewport={viewport} interaction={interaction} />

      {visibleSnapHoverScreenPoint ? (
        <div
          aria-hidden="true"
          data-testid="pdf-snap-marker"
          className="pointer-events-none absolute z-50 h-5 w-5 -translate-x-1/2 -translate-y-1/2 drop-shadow-[0_0_1px_rgba(8,47,73,0.95)]"
          style={{
            left: visibleSnapHoverScreenPoint.x,
            top: visibleSnapHoverScreenPoint.y,
          }}
        >
          <span className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-cyan-400" />
          <span className="absolute top-0 left-1/2 h-full w-px -translate-x-1/2 bg-cyan-400" />
          <span className="absolute top-1/2 left-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white bg-cyan-500" />
        </div>
      ) : null}

      {pdfSnapStatusLabel ? (
        <div
          className="pointer-events-none absolute top-3 left-1/2 z-50 -translate-x-1/2 rounded-md border border-border bg-background/90 px-2.5 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur"
          role="status"
        >
          {pdfSnapStatusLabel}
        </div>
      ) : null}

      {errorMessage ? (
        <div className="absolute top-16 left-1/2 z-90 -translate-x-1/2 rounded-lg border border-destructive bg-background/95 px-4 py-2 shadow-xl">
          <div className="text-sm text-destructive">{errorMessage}</div>
        </div>
      ) : null}

      {showDraftMeasurementCard && (draftMeasurement || (!interaction && latestDraftMeasurement && !drawingDraft)) && (
        <DraftMeasurementCard
          draft={draftMeasurement ?? latestDraftMeasurement!}
          draftTagId={draftTagId}
          tags={tags}
          onDraftTagChange={onDraftTagChange}
          onCreateCustomLineItem={onCreateCustomLineItem}
          onSaveDraftMeasurement={handleSaveDraft}
          onCancelDraftMeasurement={handleCancelDraft}
          isSavingDraftMeasurement={isSavingDraftMeasurement}
        />
      )}

      {showSelectedMeasurementCard && activeMeasurementId ? (
        <SelectedMeasurementCard
          measurement={measurements.find((m) => m._id === activeMeasurementId) ?? null}
          selectedMeasurements={selectedMeasurements}
          tags={tags}
          scale={scale}
          onSaveMeasurementEdits={handleSaveMeasurementEdits}
          onUpdateMeasurementAnnotation={onUpdateMeasurementAnnotation}
          onReassignMeasurement={onReassignMeasurement}
          onReassignMeasurements={onReassignMeasurements}
          isReassigningMeasurementId={isReassigningMeasurementId}
          isDeletingMeasurementId={isDeletingMeasurementId}
          onDeleteMeasurement={onDeleteMeasurement}
          onDeleteMeasurements={onDeleteMeasurements}
          onApplyMeasurementLeaders={onApplyMeasurementLeaders}
          onDuplicateMeasurements={onDuplicateMeasurements}
          onGroupLabels={onGroupLabels}
          onUngroupLabels={onUngroupLabels}
          onGroupAllLabelsInScope={onGroupAllLabelsInScope}
          onUngroupAllLabelsInScope={onUngroupAllLabelsInScope}
          onClose={() => onSelectMeasurement(null)}
        />
      ) : null}

      {contextMenu ? (
        <MeasurementContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          measurement={
            contextMeasurementId
              ? measurements.find((measurement) => measurement._id === contextMeasurementId) ?? null
              : null
          }
          pagePoint={contextMenu.pagePoint}
          vertexIndex={
            contextMenu.target.type === "measurement-vertex"
              ? contextMenu.target.vertexIndex
              : undefined
          }
          canPasteMeasurement={canPasteMeasurement}
          selectedMeasurementIds={selectedMeasurementIds}
          onClose={closeContextMenu}
          onCopyMeasurement={onCopyMeasurement}
          onCopyMeasurements={onCopyMeasurements}
          onPasteMeasurement={() => onPasteMeasurement?.(contextMenu.pagePoint ?? undefined)}
          onDuplicateMeasurement={(measurementId) =>
            onDuplicateMeasurement?.(measurementId, contextMenu.pagePoint ?? undefined)
          }
          onDuplicateMeasurements={(measurementIds) =>
            onDuplicateMeasurements?.(measurementIds, contextMenu.pagePoint ?? undefined)
          }
          onDeleteMeasurement={onDeleteMeasurement}
          onDeleteMeasurements={onDeleteMeasurements}
          measurements={measurements}
          onApplyMeasurementLeaders={onApplyMeasurementLeaders}
          onGroupLabels={onGroupLabels}
          onUngroupLabels={onUngroupLabels}
          onOpenMilestones={onOpenMilestones}
          onInsertPoint={handleInsertMeasurementPoint}
          onRemovePoint={handleRemoveMeasurementPoint}
        />
      ) : null}
    </div>
  );
}

export type { MeasurementOverlayProps };
